import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  getWorkOrder, updateWorkOrder, getTechnicians,
  getNotes, addNote,
  getWorkOrderParts, addPartToOrder, getParts,
  getPartRequests, createPartRequest, updatePartRequest, deletePartRequest,
  getPhotos, uploadPhoto, deletePhoto,
} from '../api/client';
import { usePermissions } from '../contexts/PermissionContext';

// ── Helpers ────────────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  pending:     { bg: '#FEF3C7', color: '#92400E' },
  assigned:    { bg: '#DBEAFE', color: '#1E40AF' },
  in_progress: { bg: '#EDE9FE', color: '#5B21B6' },
  completed:   { bg: '#D1FAE5', color: '#065F46' },
  cancelled:   { bg: '#F3F4F6', color: '#6B7280' },
};

function StatusPill({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.cancelled;
  return (
    <span style={{ ...s, padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
      {status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
    </span>
  );
}

function Card({ title, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, marginBottom: 20 }}>
      {title && (
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #F3F4F6' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {title}
          </span>
        </div>
      )}
      <div style={{ padding: '16px 20px' }}>{children}</div>
    </div>
  );
}


// ── Complete Confirm Popover ────────────────────────────────────────────────────
function CompletePopover({ onYes, onNo }) {
  return (
    <>
      {/* Backdrop — click outside to cancel */}
      <div
        onClick={onNo}
        style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
      />
      <div style={{
        position: 'fixed', bottom: 80, right: 32, zIndex: 9999,
        background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10,
        boxShadow: '0 8px 28px rgba(0,0,0,0.18)', padding: '18px 22px',
        width: 260,
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 6 }}>
          Complete this job?
        </div>
        <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 16, lineHeight: 1.5 }}>
          This will mark the work order as completed and cannot be undone.
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onYes}
            style={{ flex: 1, padding: '8px 0', borderRadius: 7, border: 'none', background: '#059669', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
          >
            Yes
          </button>
          <button
            onClick={onNo}
            style={{ flex: 1, padding: '8px 0', borderRadius: 7, border: '1px solid #D1D5DB', background: '#fff', color: '#374151', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            No
          </button>
        </div>
        {/* Arrow pointing down toward button bar */}
        <div style={{
          position: 'absolute', bottom: -7, right: 36,
          width: 13, height: 13, background: '#fff',
          border: '1px solid #E5E7EB', borderTop: 'none', borderLeft: 'none',
          transform: 'rotate(45deg)',
        }} />
      </div>
    </>
  );
}

function Grid2({ children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function TwoColumnLayout({ left, right }) {
  const [isNarrow, setIsNarrow] = useState(() => window.innerWidth < 1180);

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 1180);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: isNarrow ? '1fr' : '1.2fr 1fr',
        gap: 20,
        alignItems: 'start',
      }}
    >
      <div>{left}</div>
      <div>{right}</div>
    </div>
  );
}

const inputStyle = (readOnly) => ({
  width: '100%', boxSizing: 'border-box',
  padding: '7px 10px', fontSize: 13,
  border: '1px solid #D1D5DB', borderRadius: 6,
  background: readOnly ? '#F9FAFB' : '#fff',
  color: '#111827', outline: 'none',
});

const textareaStyle = (readOnly) => ({
  ...inputStyle(readOnly),
  resize: 'vertical', minHeight: 72,
});

function FInput({ label, field, form, setForm, readOnly, type = 'text', span2 = false, onBlur }) {
  return (
    <div style={span2 ? { gridColumn: '1 / -1' } : {}}>
      <Field label={label}>
        <input
          type={type}
          value={form[field] || ''}
          onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))}
          onBlur={onBlur}
          readOnly={readOnly}
          style={inputStyle(readOnly)}
        />
      </Field>
    </div>
  );
}

function parseAddressWO(raw) {
  const parts = (raw || '').trim().split(',').map(p => p.trim()).filter(Boolean);
  if (parts.length < 3) return null;
  const street = parts[0];
  const city = parts[1];
  if (parts.length >= 5) {
    return { street, city, state_province: parts[2], zipcode: parts[3], country: parts[4] };
  }
  if (parts.length === 4) {
    if (/^[A-Za-z]{2}$/.test(parts[2])) {
      return { street, city, state_province: parts[2], zipcode: parts[3], country: '' };
    }
    const stateZip = parts[2].match(/^([A-Za-z]{2})\s+(.+)$/);
    if (stateZip) {
      return { street, city, state_province: stateZip[1], zipcode: stateZip[2], country: parts[3] };
    }
    return { street, city, state_province: parts[2], zipcode: parts[3], country: '' };
  }
  const stateZip = parts[2].match(/^([A-Za-z]{2})\s+(\S+)$/);
  if (stateZip) {
    return { street, city, state_province: stateZip[1], zipcode: stateZip[2], country: '' };
  }
  return { street, city, country: parts[2] };
}

function FTextarea({ label, field, form, setForm, readOnly, span2 = false }) {
  return (
    <div style={span2 ? { gridColumn: '1 / -1' } : {}}>
      <Field label={label}>
        <textarea
          value={form[field] || ''}
          onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))}
          readOnly={readOnly}
          style={textareaStyle(readOnly)}
        />
      </Field>
    </div>
  );
}

function FSelect({ label, field, form, setForm, readOnly, options }) {
  return (
    <Field label={label}>
      <select
        value={form[field] || ''}
        onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))}
        disabled={readOnly}
        style={{ ...inputStyle(readOnly), cursor: readOnly ? 'default' : 'pointer' }}
      >
        <option value="">— Select —</option>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </Field>
  );
}

const btnBase = { padding: '8px 18px', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none' };

function Btn({ onClick, children, variant = 'default', disabled }) {
  const variants = {
    default:  { background: '#F3F4F6', color: '#374151' }, // Cancel 灰色
    primary:  { background: '#2563EB', color: '#fff' },    // Save 蓝色
    success:  { background: '#059669', color: '#fff' },    // Complete Job 绿色
    warning:  { background: '#D97706', color: '#fff' },    // Replacement 橙色
    danger:   { background: '#DC2626', color: '#fff' },
    outline:  { background: '#fff', color: '#2563EB', border: '1px solid #2563EB' }, // Dispatch 蓝色 outline
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{ ...btnBase, ...variants[variant], opacity: disabled ? 0.5 : 1 }}
    >
      {children}
    </button>
  );
}

function SectionHeader({ title }) {
  return (
    <div style={{ gridColumn: '1 / -1', borderBottom: '1px solid #E5E7EB', paddingBottom: 6, marginTop: 8 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {title}
      </span>
    </div>
  );
}

// ── PartReqRow (inline edit) ───────────────────────────────────────────────────

function PartReqRow({ pr, onSave, onDelete, canEdit }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ ...pr });

  if (!editing) {
    return (
      <tr style={{ borderBottom: '1px solid #F3F4F6' }}>
        <td style={tdS}>{pr.part_name}</td>
        <td style={tdS}>{pr.part_code || '—'}</td>
        <td style={tdS}>{pr.quantity}</td>
        <td style={tdS}>{pr.tracking_number || '—'}</td>
        <td style={tdS}>
          <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: PR_STATUS_BG[pr.status] || '#F3F4F6', color: PR_STATUS_COLOR[pr.status] || '#374151' }}>
            {pr.status}
          </span>
        </td>
        {canEdit && (
          <td style={tdS}>
            <button onClick={() => setEditing(true)} style={{ ...smallBtn, background: '#EFF6FF', color: '#2563EB', marginRight: 6 }}>Edit</button>
            <button onClick={() => onDelete(pr.id)} style={{ ...smallBtn, background: '#FEF2F2', color: '#DC2626' }}>Del</button>
          </td>
        )}
      </tr>
    );
  }

  return (
    <tr style={{ background: '#FAFAFA', borderBottom: '1px solid #E5E7EB' }}>
      <td style={tdS}><input value={draft.part_name} onChange={e => setDraft(p => ({ ...p, part_name: e.target.value }))} style={miniInput} /></td>
      <td style={tdS}><input value={draft.part_code || ''} onChange={e => setDraft(p => ({ ...p, part_code: e.target.value }))} style={miniInput} /></td>
      <td style={tdS}><input type="number" value={draft.quantity} onChange={e => setDraft(p => ({ ...p, quantity: +e.target.value }))} style={{ ...miniInput, width: 60 }} /></td>
      <td style={tdS}><input value={draft.tracking_number || ''} onChange={e => setDraft(p => ({ ...p, tracking_number: e.target.value }))} style={miniInput} /></td>
      <td style={tdS}>
        <select value={draft.status} onChange={e => setDraft(p => ({ ...p, status: e.target.value }))} style={miniInput}>
          {['pending', 'ordered', 'shipped', 'delivered'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </td>
      <td style={tdS}>
        <button onClick={() => { onSave(pr.id, draft); setEditing(false); }} style={{ ...smallBtn, background: '#D1FAE5', color: '#065F46', marginRight: 6 }}>Save</button>
        <button onClick={() => setEditing(false)} style={{ ...smallBtn, background: '#F3F4F6', color: '#374151' }}>Cancel</button>
      </td>
    </tr>
  );
}

const tdS = { padding: '8px 10px', fontSize: 13, color: '#374151', verticalAlign: 'middle' };
const miniInput = { padding: '4px 7px', fontSize: 12, border: '1px solid #D1D5DB', borderRadius: 5, width: '100%', boxSizing: 'border-box' };
const smallBtn = { padding: '3px 8px', fontSize: 11, fontWeight: 600, border: 'none', borderRadius: 5, cursor: 'pointer' };
const PR_STATUS_BG    = { pending: '#FEF3C7', ordered: '#DBEAFE', shipped: '#EDE9FE', delivered: '#D1FAE5' };
const PR_STATUS_COLOR = { pending: '#92400E', ordered: '#1E40AF', shipped: '#5B21B6', delivered: '#065F46' };

// ── FollowupSection ────────────────────────────────────────────────────────────

function FollowupSection({ label, field, form, setForm, readOnly }) {
  const logs = (() => {
    try { return JSON.parse(form[field] || '[]'); } catch { return []; }
  })();

  const [remark, setRemark] = useState('');

  const addLog = () => {
    if (!remark.trim()) return;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const newLog = { remark: remark.trim(), created_by: user.name || user.email || 'Unknown', created_at: new Date().toISOString() };
    setForm(p => ({ ...p, [field]: JSON.stringify([...logs, newLog]) }));
    setRemark('');
  };

  return (
    <div style={{ gridColumn: '1 / -1' }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
        {label}
      </label>
      {logs.length > 0 && (
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 7, marginBottom: 8 }}>
          {logs.map((l, i) => (
            <div key={i} style={{ padding: '8px 12px', borderBottom: i < logs.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
              <div style={{ fontSize: 13, color: '#111827' }}>{l.remark}</div>
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                {l.created_by} · {l.created_at ? new Date(l.created_at).toLocaleString() : ''}
              </div>
            </div>
          ))}
        </div>
      )}
      {!readOnly && (
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={remark}
            onChange={e => setRemark(e.target.value)}
            placeholder="Add remark…"
            style={{ ...inputStyle(false), flex: 1 }}
            onKeyDown={e => e.key === 'Enter' && addLog()}
          />
          <button onClick={addLog} style={{ ...btnBase, background: '#2563EB', color: '#fff', padding: '7px 14px', fontSize: 12 }}>
            Add
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

const TABS = ['Details'];

export default function WorkOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canEdit      = hasPermission('work_orders:edit');
  const canAddNote   = hasPermission('work_orders:notes:add');
  const canAddPart   = hasPermission('work_orders:parts:add');
  const readOnly     = !canEdit;


  const [wo, setWo]           = useState(null);
  const [form, setForm]       = useState({});
  const [techs, setTechs]     = useState([]);
  const [notes, setNotes]     = useState([]);
  const [usedParts, setUsedParts]   = useState([]);
  const [availParts, setAvailParts] = useState([]);
  const [partReqs, setPartReqs]     = useState([]);
  const [photos, setPhotos]         = useState([]);
  const [tab, setTab]               = useState('Details');
  const [saving, setSaving]         = useState(false);
  const [loading, setLoading]       = useState(true);
  const [loadError, setLoadError]   = useState('');

  // Notes state
  const [noteText, setNoteText]   = useState('');
  const [noteError, setNoteError] = useState('');

  // Parts used state
  const [newPart, setNewPart]         = useState({ part_id: '', quantity_used: 1, unit_price: '' });
  const [partError, setPartError]     = useState('');

  // Part requests state
  const [newPR, setNewPR]     = useState({ part_name: '', part_code: '', quantity: 1, tracking_number: '', status: 'pending' });
  const [prError, setPrError] = useState('');

  // Photo state
  const [photoError, setPhotoError] = useState('');
  const [photoManageMode, setPhotoManageMode] = useState(false);

  // Complete confirm popover
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [notice, setNotice] = useState(null); // { msg, type }
  const noticeRef = React.useRef(null);
  const notify = (msg, type = 'success') => {
    clearTimeout(noticeRef.current);
    setNotice({ msg, type });
    noticeRef.current = setTimeout(() => setNotice(null), 3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const woRes = await getWorkOrder(id);
      const woData = woRes.data;
      setWo(woData);
      setForm({ ...woData });

      const [techsRes, notesRes, usedRes, prRes, photosRes] = await Promise.all([
        getTechnicians().catch(() => ({ data: [] })),
        getNotes(id).catch(() => ({ data: [] })),
        getWorkOrderParts(id).catch(() => ({ data: [] })),
        getPartRequests(id).catch(() => ({ data: [] })),
        getPhotos(id).catch(() => ({ data: [] })),
      ]);
      setTechs(techsRes.data || []);
      setNotes(notesRes.data || []);
      setUsedParts(usedRes.data || []);
      setPartReqs(prRes.data || []);
      setPhotos(photosRes.data || []);

      if (canAddPart) {
        const partsRes = await getParts({}).catch(() => ({ data: [] }));
        setAvailParts(partsRes.data || []);
      }
    } catch (e) {
      setLoadError(e.response?.data?.detail || 'Failed to load work order.');
    } finally {
      setLoading(false);
    }
  }, [id, canAddPart]);

  useEffect(() => { load(); }, [load]);

  const errMsg = (e) => {
    const d = e.response?.data?.detail;
    if (!d) return '操作失敗';
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map(x => x.msg || JSON.stringify(x)).join('; ');
    return JSON.stringify(d);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateWorkOrder(id, form);
      setWo(res.data);
      setForm({ ...res.data });
      notify('保存成功', 'success');
    } catch (e) {
      notify(errMsg(e), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDispatch = async () => {
    if (!form.technician_id) {
      notify('請先選擇技術員再派工', 'error');
      return;
    }
    setSaving(true);
    try {
      const res = await updateWorkOrder(id, { ...form, status: 'assigned' });
      setWo(res.data); setForm({ ...res.data });
      notify('派工成功', 'success');
    } catch (e) {
      notify(errMsg(e), 'error');
    } finally { setSaving(false); }
  };

  const handleSubmit = () => {
    if (!form.follow_up?.trim()) {
      notify('請先填寫 Follow Up 備註才能完結工單', 'error');
      return;
    }
    setShowCompleteConfirm(true);
  };

  const doComplete = async () => {
    setShowCompleteConfirm(false);
    setSaving(true);
    try {
      const res = await updateWorkOrder(id, { ...form, status: 'completed' });
      setWo(res.data); setForm({ ...res.data });
      toast.success('Work order completed');
    } catch (e) {
      notify(errMsg(e), 'error');
    } finally { setSaving(false); }
  };

  const handleReplacement = async () => {
    setSaving(true);
    try {
      const res = await updateWorkOrder(id, { ...form, work_order_type: 'Replacement' });
      setWo(res.data); setForm({ ...res.data });
      notify('已標記為換機', 'success');
    } catch (e) {
      notify(errMsg(e), 'error');
    } finally { setSaving(false); }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setNoteError('');
    try {
      await addNote(id, { content: noteText.trim() });
      setNoteText('');
      const res = await getNotes(id);
      setNotes(res.data || []);
    } catch (e) { setNoteError(e.response?.data?.detail || 'Failed to add note.'); }
  };

  const handleAddPart = async () => {
    if (!newPart.part_id || !newPart.unit_price) { setPartError('Select part and enter price.'); return; }
    setPartError('');
    try {
      await addPartToOrder(id, { ...newPart, quantity_used: +newPart.quantity_used, unit_price: +newPart.unit_price });
      setNewPart({ part_id: '', quantity_used: 1, unit_price: '' });
      const res = await getWorkOrderParts(id);
      setUsedParts(res.data || []);
    } catch (e) { setPartError(e.response?.data?.detail || 'Failed to add part.'); }
  };

  const handleAddPR = async () => {
    if (!newPR.part_name) { setPrError('Part name required.'); return; }
    setPrError('');
    try {
      await createPartRequest(id, { ...newPR, quantity: +newPR.quantity });
      setNewPR({ part_name: '', part_code: '', quantity: 1, tracking_number: '', status: 'pending' });
      const res = await getPartRequests(id);
      setPartReqs(res.data || []);
    } catch (e) { setPrError(e.response?.data?.detail || 'Failed to create request.'); }
  };

  const handleSavePR = async (prId, data) => {
    try {
      await updatePartRequest(id, prId, data);
      const res = await getPartRequests(id);
      setPartReqs(res.data || []);
    } catch (e) { setPrError(e.response?.data?.detail || 'Failed to update.'); }
  };

  const handleDeletePR = async (prId) => {
    try {
      await deletePartRequest(id, prId);
      setPartReqs(p => p.filter(r => r.id !== prId));
    } catch (e) { setPrError(e.response?.data?.detail || 'Failed to delete.'); }
  };

  const handleUploadPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoError('');
    const fd = new FormData();
    fd.append('file', file);
    try {
      await uploadPhoto(id, fd);
      const res = await getPhotos(id);
      setPhotos(res.data || []);
    } catch (err) { setPhotoError(err.response?.data?.detail || 'Upload failed.'); }
    e.target.value = '';
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await deletePhoto(id, photoId);
      setPhotos(p => p.filter(ph => ph.id !== photoId));
    } catch (e) { setPhotoError(e.response?.data?.detail || 'Delete failed.'); }
  };

  if (loading) return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: 40, textAlign: 'center', color: '#6B7280' }}>
      Loading work order…
    </div>
  );

  if (loadError) return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: 40 }}>
      <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: 16, color: '#DC2626' }}>{loadError}</div>
      <button onClick={() => navigate('/work-orders')} style={{ marginTop: 12, ...btnBase, background: '#F3F4F6', color: '#374151' }}>
        ← Back to Work Orders
      </button>
    </div>
  );

  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:8000';

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>

      {/* Page Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <button
          onClick={() => navigate('/work-orders')}
          style={{ ...btnBase, background: '#F3F4F6', color: '#374151', fontSize: 12, padding: '6px 14px' }}
        >
          ← Back
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#111827' }}>
              {wo?.job_number}
            </h1>
            <StatusPill status={wo?.status} />
          </div>
          <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>
            {wo?.customer_name} · {wo?.address}
          </div>
        </div>
      </div>

      {/* Sticky Tab Bar */}
      <div style={{
        position: 'sticky', top: -24, zIndex: 20,
        background: '#F9FAFB', margin: '0 -24px',
        padding: '0 24px',
        borderBottom: '2px solid #E5E7EB',
        display: 'flex', gap: 2,
      }}>
        {TABS.map(t => {
          const count =
            t === 'Notes' ? notes.length :
            t === 'Parts Used' ? usedParts.length :
            t === 'Parts Req.' ? partReqs.length :
            t === 'Attachments' ? photos.length : null;
          return (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                padding: '12px 18px', fontSize: 13, fontWeight: tab === t ? 700 : 500,
                border: 'none', borderBottom: tab === t ? '3px solid #2563EB' : '3px solid transparent',
                background: 'none', color: tab === t ? '#2563EB' : '#6B7280',
                cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              {t}{count !== null ? ` (${count})` : ''}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div style={{ paddingTop: 24 }}>

        {/* ── DETAILS TAB ────────────────────────────────────────────────── */}
        {tab === 'Details' && (
          <TwoColumnLayout
            left={
              <>
                <Card title="Basic Information">
                  <Grid2>
                    <FInput label="Phone" field="customer_phone" form={form} setForm={setForm} readOnly={readOnly} />
                    <FInput label="Email" field="customer_email" form={form} setForm={setForm} readOnly={readOnly} />

                    <FSelect
                      label="Priority"
                      field="priority"
                      form={form} setForm={setForm} readOnly={readOnly}
                      options={[
                        { value: 'low', label: 'Low' },
                        { value: 'normal', label: 'Normal' },
                        { value: 'high', label: 'High' },
                        { value: 'urgent', label: 'Urgent' },
                      ]}
                    />

                    <Field label="Status">
                      <select
                        value={form.status || ''}
                        onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
                        disabled={readOnly}
                        style={{ ...inputStyle(readOnly), cursor: readOnly ? 'default' : 'pointer' }}
                      >
                        {['pending','assigned','in_progress','completed','cancelled'].map(s => (
                          <option key={s} value={s}>{s.replace('_', ' ')}</option>
                        ))}
                      </select>
                    </Field>

                    <Field label="Scheduled At">
                      <input
                        type="datetime-local"
                        value={form.scheduled_at ? form.scheduled_at.slice(0, 16) : ''}
                        onChange={e => setForm(p => ({ ...p, scheduled_at: e.target.value }))}
                        readOnly={readOnly}
                        style={inputStyle(readOnly)}
                      />
                    </Field>

                    <FInput label="Labor Cost ($)" field="labor_cost" type="number" form={form} setForm={setForm} readOnly={readOnly} />

                    <SectionHeader title="Business / Customer Information" />

                    <FInput label="Business Name" field="business_name" form={form} setForm={setForm} readOnly={readOnly} />
                    <FInput label="Contact Name" field="contact_name" form={form} setForm={setForm} readOnly={readOnly} />
                    {/* Paste helper — full width, uncontrolled, clears after parse */}
                    <div style={{ gridColumn: '1 / -1' }}>
                      <Field label="Paste Full Address (auto-fills fields below)">
                        <input
                          disabled={readOnly}
                          placeholder="e.g. 168 Rosswell St, Marietta, GA, 30060"
                          value={[form.address, form.city, form.state_province, form.zipcode, form.country].filter(Boolean).join(', ')}
                          onChange={e => {
                            const parsed = parseAddressWO(e.target.value);
                            if (parsed) {
                              setForm(p => ({
                                ...p,
                                address:        parsed.street         || p.address,
                                city:           parsed.city           || p.city,
                                state_province: parsed.state_province || p.state_province,
                                zipcode:        parsed.zipcode        || p.zipcode,
                                country:        parsed.country        || p.country,
                              }));
                            }
                          }}
                          style={inputStyle(readOnly)}
                        />
                      </Field>
                    </div>
                    <FInput label="Country" field="country" form={form} setForm={setForm} readOnly={readOnly} />
                    <FInput label="Street" field="address" form={form} setForm={setForm} readOnly={readOnly} />
                    <FInput label="City" field="city" form={form} setForm={setForm} readOnly={readOnly} />
                    <FInput label="State / Province" field="state_province" form={form} setForm={setForm} readOnly={readOnly} />
                    <FInput label="Zip Code" field="zipcode" form={form} setForm={setForm} readOnly={readOnly} />

                    <FTextarea label="Problem Description" field="problem_description" form={form} setForm={setForm} readOnly={readOnly} span2 />
                    <FTextarea label="Diagnosis / Repair Action" field="diagnosis" form={form} setForm={setForm} readOnly={readOnly} span2 />
                    
                  </Grid2>
                </Card>

                <Card title="Diagnosis">
                  <Grid2>
                    <FTextarea label="Pre-diagnosis Result" field="prediagnosis_result" form={form} setForm={setForm} readOnly={readOnly} span2 />
                    <FTextarea label="Diagnosis Routes" field="diagnosis_routes" form={form} setForm={setForm} readOnly={readOnly} span2 />
                  </Grid2>
                </Card>

                <Card title="技術員完工備注">
                  <Grid2>
                    <FTextarea label="完工說明 (Resolution)" field="resolution" form={form} setForm={setForm} readOnly={readOnly} span2 />
                    <FTextarea label="技術員備注 (Technician Notes)" field="technician_notes" form={form} setForm={setForm} readOnly={readOnly} span2 />
                  </Grid2>
                </Card>

                <Card title="Follow-up Logs">
                  <Grid2>
                    <SectionHeader title="Technician Follow-up" />
                    <FollowupSection label="" field="tech_followup" form={form} setForm={setForm} readOnly={readOnly} />
                    <SectionHeader title="Dealer Follow-up" />
                    <FollowupSection label="" field="dealer_followup" form={form} setForm={setForm} readOnly={readOnly} />
                    <SectionHeader title="Custom Follow-up" />
                    <FollowupSection label="" field="custom_followup" form={form} setForm={setForm} readOnly={readOnly} />
                  </Grid2>
                </Card>
              </>
            }
            right={
              <>
                <Card title="Follow Up">
                  <FTextarea
                    label="Closure / Follow-up Note (required to complete)"
                    field="follow_up"
                    form={form}
                    setForm={setForm}
                    readOnly={readOnly}
                    span2
                  />
                </Card>

                <Card title="Warranty Information">
                  <Grid2>
                    <FSelect
                      label="Warranty Status"
                      field="warranty_status"
                      form={form} setForm={setForm} readOnly={readOnly}
                      options={[
                        { value: 'in_warranty', label: 'In Warranty' },
                        { value: 'out_of_warranty', label: 'Out of Warranty' },
                        { value: 'extended', label: 'Extended' },
                      ]}
                    />
                    <FInput label="Extended Warranty Details" field="extended_warranty" form={form} setForm={setForm} readOnly={readOnly} />
                    <FInput label="Invoice Date" field="invoice_date" form={form} setForm={setForm} readOnly={readOnly} />
                  </Grid2>
                </Card>

                <Card title="Unit Information">
                  <Grid2>
                    <FInput label="Equipment Type" field="equipment_type" form={form} setForm={setForm} readOnly={readOnly} />
                    <FInput label="Brand" field="equipment_brand" form={form} setForm={setForm} readOnly={readOnly} />
                    <FInput label="Model" field="equipment_model" form={form} setForm={setForm} readOnly={readOnly} />
                    <FInput label="Serial Number" field="equipment_serial" form={form} setForm={setForm} readOnly={readOnly} />
                    <FInput label="Work Order Type" field="work_order_type" form={form} setForm={setForm} readOnly={readOnly} />
                    <FTextarea label="Unit Description" field="unit_description" form={form} setForm={setForm} readOnly={readOnly} span2 />
                  </Grid2>
                </Card>

                <Card title="Technician Information">
                  <Grid2>
                    <Field label="Assigned Technician">
                      <select
                        value={form.technician_id || ''}
                        onChange={e => setForm(p => ({ ...p, technician_id: e.target.value }))}
                        disabled={readOnly}
                        style={{ ...inputStyle(readOnly), cursor: readOnly ? 'default' : 'pointer' }}
                      >
                        <option value="">— Unassigned —</option>
                        {techs.map(t => (
                          <option key={t.id} value={t.id}>{t.full_name || t.name}</option>
                        ))}
                      </select>
                    </Field>

                    <FSelect
                      label="Technician Level"
                      field="technician_level"
                      form={form} setForm={setForm} readOnly={readOnly}
                      options={[
                        { value: 'junior', label: 'Junior' },
                        { value: 'mid', label: 'Mid' },
                        { value: 'senior', label: 'Senior' },
                        { value: 'lead', label: 'Lead' },
                      ]}
                    />

                    <FSelect
                      label="Technician Type"
                      field="technician_type"
                      form={form} setForm={setForm} readOnly={readOnly}
                      options={[
                        { value: 'internal', label: 'Internal' },
                        { value: 'contractor', label: 'Contractor' },
                        { value: 'partner', label: 'Partner' },
                      ]}
                    />
                  </Grid2>
                </Card>

                <Card title="Parts Used">
                  {usedParts.length > 0 ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                      <thead>
                        <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                          {['Part', 'Qty', 'Unit Price', 'Total'].map(h => (
                            <th key={h} style={{ ...tdS, fontWeight: 600, fontSize: 12, color: '#6B7280', textAlign: 'left' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {usedParts.map(p => (
                          <tr key={p.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                            <td style={tdS}>{p.part_name}</td>
                            <td style={tdS}>{p.quantity_used}</td>
                            <td style={tdS}>${p.unit_price?.toFixed(2)}</td>
                            <td style={tdS}>${p.total_price?.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ color: '#9CA3AF', fontSize: 13 }}>No parts used.</div>
                  )}

                  {canAddPart && (
                    <div style={{ marginTop: 12, padding: '14px 16px', background: '#F9FAFB', borderRadius: 8, border: '1px solid #E5E7EB' }}>
                      <div style={{ fontWeight: 600, fontSize: 12, color: '#374151', marginBottom: 10 }}>Add Part</div>
                      {partError && <div style={{ color: '#DC2626', fontSize: 12, marginBottom: 8 }}>{partError}</div>}
                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 80px 120px auto', gap: 8, alignItems: 'end' }}>
                        <Field label="Part">
                          <select
                            value={newPart.part_id}
                            onChange={e => {
                              const p = availParts.find(x => x.id === e.target.value);
                              setNewPart(prev => ({ ...prev, part_id: e.target.value, unit_price: p ? p.unit_price : '' }));
                            }}
                            style={inputStyle(false)}
                          >
                            <option value="">Select…</option>
                            {availParts.map(p => (
                              <option key={p.id} value={p.id}>{p.name} (stock: {p.quantity_in_stock})</option>
                            ))}
                          </select>
                        </Field>
                        <Field label="Qty">
                          <input
                            type="number"
                            min={1}
                            value={newPart.quantity_used}
                            onChange={e => setNewPart(p => ({ ...p, quantity_used: e.target.value }))}
                            style={inputStyle(false)}
                          />
                        </Field>
                        <Field label="Unit Price">
                          <input
                            type="number"
                            step="0.01"
                            value={newPart.unit_price}
                            onChange={e => setNewPart(p => ({ ...p, unit_price: e.target.value }))}
                            style={inputStyle(false)}
                          />
                        </Field>
                        <div style={{ paddingBottom: 1 }}>
                          <Btn onClick={handleAddPart} variant="primary">Add</Btn>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>

                <Card title="Parts Requests">
                  {partReqs.length > 0 ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                      <thead>
                        <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                          {['Part Name', 'Code', 'Qty', 'Tracking #', 'Status', canEdit ? 'Actions' : ''].map((h, i) => (
                            <th key={i} style={{ ...tdS, fontWeight: 600, fontSize: 12, color: '#6B7280', textAlign: 'left' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {partReqs.map(pr => (
                          <PartReqRow
                            key={pr.id}
                            pr={pr}
                            onSave={handleSavePR}
                            onDelete={handleDeletePR}
                            canEdit={canEdit}
                          />
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ color: '#9CA3AF', fontSize: 13 }}>No part requests.</div>
                  )}

                  {canEdit && (
                    <div style={{ marginTop: 12, padding: '14px 16px', background: '#F9FAFB', borderRadius: 8, border: '1px solid #E5E7EB' }}>
                      <div style={{ fontWeight: 600, fontSize: 12, color: '#374151', marginBottom: 10 }}>New Request</div>
                      {prError && <div style={{ color: '#DC2626', fontSize: 12, marginBottom: 8 }}>{prError}</div>}
                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 80px 1fr 120px auto', gap: 8, alignItems: 'end' }}>
                        <Field label="Part Name">
                          <input value={newPR.part_name} onChange={e => setNewPR(p => ({ ...p, part_name: e.target.value }))} style={inputStyle(false)} />
                        </Field>
                        <Field label="Part Code">
                          <input value={newPR.part_code} onChange={e => setNewPR(p => ({ ...p, part_code: e.target.value }))} style={inputStyle(false)} />
                        </Field>
                        <Field label="Qty">
                          <input type="number" min={1} value={newPR.quantity} onChange={e => setNewPR(p => ({ ...p, quantity: +e.target.value }))} style={inputStyle(false)} />
                        </Field>
                        <Field label="Tracking #">
                          <input value={newPR.tracking_number} onChange={e => setNewPR(p => ({ ...p, tracking_number: e.target.value }))} style={inputStyle(false)} />
                        </Field>
                        <Field label="Status">
                          <select value={newPR.status} onChange={e => setNewPR(p => ({ ...p, status: e.target.value }))} style={inputStyle(false)}>
                            {['pending', 'ordered', 'shipped', 'delivered'].map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </Field>
                        <div style={{ paddingBottom: 1 }}>
                          <Btn onClick={handleAddPR} variant="primary">Add</Btn>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>

                <Card title={`Attachments (${photos.length})`}>
                  {photoError && (
                    <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6, padding: '8px 12px', color: '#DC2626', fontSize: 12, marginBottom: 12 }}>
                      {photoError}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                    {canEdit && (
                      <label style={{ display: 'inline-block', padding: '8px 16px', background: '#2563EB', color: '#fff', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        Upload Attachment
                        <input type="file" accept="image/*" onChange={handleUploadPhoto} style={{ display: 'none' }} />
                      </label>
                    )}
                    <button
                      onClick={() => setPhotoManageMode(m => !m)}
                      style={{ padding: '8px 16px', background: photoManageMode ? '#FEF2F2' : '#F3F4F6', color: photoManageMode ? '#DC2626' : '#374151', border: photoManageMode ? '1px solid #FECACA' : '1px solid #E5E7EB', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                    >
                      {photoManageMode ? 'Done' : 'Manage'}
                    </button>
                  </div>

                  {photos.length === 0 ? (
                    <div style={{ color: '#9CA3AF', fontSize: 13 }}>No attachments.</div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                      {photos.map(ph => (
                        <div key={ph.id} style={{ border: photoManageMode ? '2px solid #FECACA' : '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                          <img
                            src={`${API_BASE}${ph.url}`}
                            alt={ph.caption || 'attachment'}
                            onClick={() => window.open(`${API_BASE}${ph.url}`, '_blank')}
                            style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block', cursor: 'zoom-in' }}
                          />
                          {ph.caption && (
                            <div style={{ padding: '5px 8px', fontSize: 11, color: '#6B7280', background: '#F9FAFB' }}>{ph.caption}</div>
                          )}
                          {photoManageMode && canEdit && (
                            <button
                              onClick={() => handleDeletePhoto(ph.id)}
                              style={{ display: 'block', width: '100%', background: '#FEE2E2', color: '#DC2626', border: 'none', padding: '8px 0', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </>
            }
          />
        )}

        <div style={{ height: 80 }} />
      </div>

      {/* ── Sticky Action Bar ─────────────────────────────────────────────── */}
      <div style={{
        position: 'sticky', bottom: -24, zIndex: 20,
        background: '#fff', borderTop: '1px solid #E5E7EB',
        margin: '0 -24px', padding: '12px 24px',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <Btn onClick={() => navigate('/work-orders')} variant="default">
          Cancel
        </Btn>

        {canEdit && (
          <>
            <Btn onClick={handleSave} variant="primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Btn>

            <Btn onClick={handleDispatch} variant="outline" disabled={saving}>
              Dispatch
            </Btn>

            <Btn onClick={handleSubmit} variant="success" disabled={saving}>
              Complete Job
            </Btn>
            {showCompleteConfirm && (
              <CompletePopover onYes={doComplete} onNo={() => setShowCompleteConfirm(false)} />
            )}

            <Btn onClick={handleReplacement} variant="warning" disabled={saving}>
              Replacement
            </Btn>
          </>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16 }}>
          {notice && (
            <span style={{
              padding: '6px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600, color: '#fff',
              background: notice.type === 'success' ? '#059669' : '#DC2626',
            }}>
              {notice.msg}
            </span>
          )}
          <div style={{ display: 'flex', gap: 20, fontSize: 12, color: '#6B7280' }}>
            <span>Labor: <strong style={{ color: '#111827' }}>${wo?.labor_cost?.toFixed(2) || '0.00'}</strong></span>
            <span>Parts: <strong style={{ color: '#111827' }}>${wo?.parts_cost?.toFixed(2) || '0.00'}</strong></span>
            <span>Total: <strong style={{ color: '#111827', fontSize: 14 }}>${wo?.total_cost?.toFixed(2) || '0.00'}</strong></span>
          </div>
        </div>

      </div>
    </div>
  );
}